import { Divider, Stack, Switch } from '@mui/material';
import { memo, useCallback, useMemo, useRef } from 'react';
import type { ChangeEvent, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { logger } from '@common/logging/logger';
import { PropertiesPopup } from '@/modules/common/components/PropertiesPopup';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';
import { downloadFile } from '@/modules/common/helpers/browser';
import { useFileTypeValidator } from '@/modules/common/hooks/useFileValidator';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { ValidAssetType } from '@/modules/vehicles';
import { useVehicleAssetFileUpload } from '@/modules/vehicles/hooks/useVehicleAssetFileUpload';
import { VehicleServiceFactory } from '@/modules/vehicles/service/VehicleServiceFactory';
import { PROJECT_FILES_ROOT_CONTAINER } from '@/modules/api/constant';
import { FileApiConnector } from '@/modules/api/FileApiConnector/FileApiConnector';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { isShuttleLoadCarrierId } from '../../helpers/shuttleSelectionHelpers';
import FileActions from './FileActions';

type AssetPanelProps = {
  anchor: MutableRefObject<HTMLDivElement | null>;
  vehicleId: string;
  close: () => void;
};

type DownloadConfig = {
  mimeType: string;
  fileName: string;
};

const getDownloadConfig = (assetType: ValidAssetType, name: string): DownloadConfig | null => {
  switch (assetType) {
    case 'uvt':
      return {
        mimeType: 'application/json',
        fileName: `${name}.json`,
      };

    case 'km_mdb':
      return {
        mimeType: 'application/vnd.ms-access',
        fileName: `${name}.mdb`,
      };

    default:
      return null;
  }
};

const isValidBlob = (data: Blob | null | undefined) => data !== null && data !== undefined;

const AssetPanelComponent = ({ anchor, vehicleId, close }: AssetPanelProps) => {
  const { t } = useTranslation('interface');
  const { uploadAndSyncFileToFloorplan } = useVehicleAssetFileUpload();
  const { validateFileType } = useFileTypeValidator();
  const { getVehicleTypeById } = useFloorplanDesignStore();

  const uvtFileInput = useRef<HTMLInputElement>(null);
  const kmMdbFileInput = useRef<HTMLInputElement>(null);
  const vehicleType = getVehicleTypeById(vehicleId);
  const isShuttleLoadCarrier = useMemo(() => isShuttleLoadCarrierId(vehicleId), [vehicleId]);
  const obstacleAvoidanceEnabled = useMemo(
    () => vehicleType?.splineOptimizerSettings?.enableObstacleAvoidance ?? false,
    [vehicleType],
  );

  const fileApi = useMemo(() => FileApiConnector.getInstance(), []);
  const vehicleSvc = useMemo(() => VehicleServiceFactory.getInstance().createVehicleService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const download = useCallback(
    async (fileRef: string, assetType: ValidAssetType, name: string) => {
      try {
        const basePath = buildRevisionFileStorageBasePath();
        if (!basePath) {
          logger.error('basePath not defined.');
          return;
        }

        const downloadConfig = getDownloadConfig(assetType, name);

        if (!downloadConfig) {
          logger.error(`Unsupported asset type: ${assetType}`);
          return;
        }

        const response = await fileApi.getBlobFileByUrl(
          `${PROJECT_FILES_ROOT_CONTAINER}?filename=${basePath}/${fileRef}`,
        );

        if (!isValidBlob(response)) {
          logger.error('Invalid file response, download skipped.');
          return;
        }

        downloadFile(response, downloadConfig.mimeType, downloadConfig.fileName);
      } catch (error) {
        logger.error('Error downloading file', error);
      }
    },
    [fileApi],
  );

  const onFileSelect = useCallback(
    (assetType: ValidAssetType, event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      event.target.value = '';

      if (!file) {
        return;
      }

      if (validateFileType(file, [assetType === 'uvt' ? 'json' : 'mdb'])) {
        uploadAndSyncFileToFloorplan(file, assetType, vehicleId);
      }
    },
    [uploadAndSyncFileToFloorplan, validateFileType, vehicleId],
  );

  const handleUploadClick = useCallback((assetType: ValidAssetType) => {
    if (assetType === 'uvt') {
      uvtFileInput.current?.click();
      return;
    }

    if (assetType === 'km_mdb') {
      kmMdbFileInput.current?.click();
    }
  }, []);

  const handleReset = useCallback(
    async (assetType: ValidAssetType) => {
      try {
        const {
          metaData: { projectId, alternativeId, revisionId },
        } = useFloorplanDesignStore.getState();

        await vehicleSvc.getLatestFileRef(
          assetType,
          projectId,
          alternativeId,
          revisionId,
          vehicleId,
        );
      } catch (error) {
        logger.error('Error resetting vehicle asset file', error);
      }
    },
    [vehicleId, vehicleSvc],
  );

  const handleUvtUpload = useCallback(() => handleUploadClick('uvt'), [handleUploadClick]);

  const handleKmMdbUpload = useCallback(() => handleUploadClick('km_mdb'), [handleUploadClick]);
  // PLZ FIX ME
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleUvtDownload = useCallback(() => {
    if (!vehicleType?.uvtFileRef) {
      return;
    }

    download(vehicleType.uvtFileRef, 'uvt', vehicleId);
  }, [download, vehicleId, vehicleType?.uvtFileRef]);

  // PLZ FIX ME
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleKmMdbDownload = useCallback(() => {
    if (!vehicleType?.kmMdbFileRef) {
      return;
    }

    download(vehicleType.kmMdbFileRef, 'km_mdb', vehicleId);
  }, [download, vehicleId, vehicleType?.kmMdbFileRef]);

  const handleUvtReset = useCallback(() => {
    handleReset('uvt');
  }, [handleReset]);

  const handleKmMdbReset = useCallback(() => {
    handleReset('km_mdb');
  }, [handleReset]);

  const handleUvtFileSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onFileSelect('uvt', event),
    [onFileSelect],
  );

  const handleKmMdbFileSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onFileSelect('km_mdb', event),
    [onFileSelect],
  );

  const handleObstacleAvoidanceChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!vehicleType) return;

      fddmSvc.updateVehicleType({
        ...vehicleType,
        splineOptimizerSettings: {
          enableObstacleAvoidance: event.target.checked,
        },
      });
    },
    [fddmSvc, vehicleType],
  );

  if (!vehicleType) return null;

  return (
    <PropertiesPopup
      anchor={anchor}
      open
      onClose={close}
      header={
        <PropertiesTitle
          fontSize={14}
          fontWeight={400}
          value={t('setup.floorPlan.vehicle_types.asset_panel.title')}
        />
      }
      sx={{
        width: '320px',
        height: 'auto',
      }}
    >
      <Stack spacing='10px'>
        <Stack
          divider={<Divider />}
          gap={1}
          sx={{
            maxHeight: '200px',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {vehicleType.uvtFileRef && (
            <>
              <FileActions
                fileName={vehicleType.uvtFileRefOriginal ? vehicleId : `custom-${vehicleId}`}
                fileType='json'
                onUpload={handleUvtUpload}
                onDownload={handleUvtDownload}
                onReset={handleUvtReset}
              />
              <input
                type='file'
                accept='.json'
                ref={uvtFileInput}
                onChange={handleUvtFileSelect}
                hidden
              />
            </>
          )}
          {vehicleType.kmMdbFileRef && (
            <>
              <FileActions
                fileName={vehicleType.kmMdbFileRefOriginal ? vehicleId : `custom-${vehicleId}`}
                fileType='mdb'
                onUpload={handleKmMdbUpload}
                onDownload={handleKmMdbDownload}
                onReset={handleKmMdbReset}
              />
              <input
                type='file'
                accept='.mdb'
                ref={kmMdbFileInput}
                onChange={handleKmMdbFileSelect}
                hidden
              />
            </>
          )}
          {!isShuttleLoadCarrier && (
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={0}>
              <PropertiesLabel
                i18nkey='interface:setup.floorPlan.vehicle_types.asset_panel.obstacle_avoidance.label'
                labelRatio={70}
              >
                <Stack direction='row' justifyContent='flex-end'>
                  <Switch
                    checked={obstacleAvoidanceEnabled}
                    onChange={handleObstacleAvoidanceChange}
                  />
                </Stack>
              </PropertiesLabel>
            </Stack>
          )}
        </Stack>
      </Stack>
    </PropertiesPopup>
  );
};

export const AssetPanel = memo(AssetPanelComponent);
